<template>
  <b-card
    v-if="data"
    class="tobe-widget-card"
    no-body
  >
    <b-card-header class="tobe-header">
      <h4 class="tobe-widget-title">
        To be Invoices
      </h4>
      <div
        v-if="lastUpdatedAt"
        class="tobe-header-right"
        :class="{ 'clickable': onRefresh }"
        @click="handleRefresh"
      >
        <feather-icon
          icon="RefreshCwIcon"
          size="12"
          class="mr-1"
          :class="{ 'spin': isRefreshing }"
        />
        <span v-if="lastRefreshTimeAgo">
          {{ lastRefreshTimeAgo }}
        </span>
        <span v-else>
          {{ __dateTimeFormatterPassedTime(lastUpdatedAt) }}
        </span>
      </div>
    </b-card-header>
    <b-card-body class="tobe-widget-body">
      <b-row class="tobe-widget-grid">
        <b-col
          v-for="transaction in data"
          :key="transaction.mode"
          md="6"
          sm="12"
          class="tobe-widget-col"
        >
          <div
            class="tobe-card"
            :class="getCardClass(transaction)"
          >
            <div class="tobe-card-icon">
              <feather-icon
                :icon="transaction.avatar"
                size="28"
              />
            </div>
            <div class="tobe-card-text">
              <div
                v-b-tooltip.hover.v-primary
                class="tobe-card-value"
                :title="__numberWithCommas(transaction.payment, 2)"
              >
                {{ __numberWithCommas(kFormatter(transaction.payment)) }}
              </div>
              <div class="tobe-card-label">
                {{ transaction.mode }}
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  VBTooltip,
} from 'bootstrap-vue'
import { kFormatter } from '@core/utils/filter'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    lastUpdatedAt: {
      type: String,
      default: null,
    },
    onRefresh: {
      type: Function,
      default: null,
    },
    isRefreshing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentTime: Date.now(),
      updateInterval: null,
    }
  },
  computed: {
    lastRefreshTimeAgo() {
      try {
        const refreshTimes = JSON.parse(localStorage.getItem('dashboardwidgetsrefreshtimes') || '{}')
        const lastRefreshTime = refreshTimes.tobeinvoices
        if (!lastRefreshTime) return null

        const lastRefresh = new Date(lastRefreshTime).getTime()
        const now = this.currentTime
        const diff = now - lastRefresh

        if (diff < 0) return null

        const seconds = Math.floor(diff / 1000)
        const minutes = Math.floor(seconds / 60)
        const hours = Math.floor(minutes / 60)
        const days = Math.floor(hours / 24)
        const months = Math.floor(days / 30)
        const years = Math.floor(months / 12)

        if (minutes < 1) return 'just now'
        if (minutes < 60) return `${minutes} minutes ago`
        if (hours < 24) return `${hours} hours ago`
        if (days < 30) return `${days} days ago`
        if (months < 12) return `${months} months ago`
        return `${years} years ago`
      } catch (e) {
        return null
      }
    },
  },
  mounted() {
    // Update countdown every second
    this.updateInterval = setInterval(() => {
      this.currentTime = Date.now()
    }, 1000)
  },
  beforeDestroy() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
    }
  },
  methods: {
    kFormatter,
    handleRefresh() {
      if (this.onRefresh && !this.isRefreshing) {
        this.onRefresh()
      }
    },
    getCardClass(transaction) {
      const mode = transaction.mode ? transaction.mode.toLowerCase() : ''
      if (mode.includes('cod') || mode.includes('collected')) {
        return 'tobe-card--cod'
      }
      if (mode.includes('delivery') || mode.includes('charge')) {
        return 'tobe-card--delivery'
      }
      if (mode.includes('commission')) {
        return 'tobe-card--commission'
      }
      if (mode.includes('invoice') || mode.includes('value')) {
        return 'tobe-card--invoice'
      }
      // Fallback to avatarVariant if mode doesn't match
      if (transaction.avatarVariant === 'light-warning') {
        return 'tobe-card--cod'
      }
      if (transaction.avatarVariant === 'light-success') {
        return 'tobe-card--delivery'
      }
      if (transaction.avatarVariant === 'light-info') {
        return 'tobe-card--commission'
      }
      if (transaction.avatarVariant === 'light-primary') {
        return 'tobe-card--invoice'
      }
      return 'tobe-card--cod'
    },
  },
}
</script>

<style scoped>
.tobe-widget-card {
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  border-radius: 0.857rem;
  overflow: hidden;
  height: 100%;
}

.tobe-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  background: #fff;
  margin-bottom: 0;
}

.tobe-widget-title {
  font-size: 1.285rem;
  font-weight: 600;
  color: #5e5873;
  margin: 0;
  font-family: 'Roboto', sans-serif;
}

.tobe-header-right {
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  color: #6e6b7b;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s ease;
  font-family: 'Roboto', sans-serif;
}

.tobe-header-right.clickable {
  cursor: pointer;
  user-select: none;
}

.tobe-header-right.clickable:hover {
  background-color: #e8e8e8;
  transform: scale(1.05);
}

.tobe-header-right.clickable:active {
  transform: scale(0.98);
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.tobe-widget-body {
  padding: 1.5rem;
}

.tobe-widget-grid {
  margin: 0;
}

.tobe-widget-col {
  padding: 0.5rem;
  display: flex;
}

.tobe-widget-col:nth-child(odd) {
  padding-left: 0.15rem;
}

.tobe-widget-col:nth-child(even) {
  padding-right: 0.15rem;
}

.tobe-card {
  border-radius: 14px;
  padding: 16px 20px;
  width: 100%;
  height: 120px;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.tobe-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.1);
}

.tobe-card::before {
  content: "";
  position: absolute;
  left: -30px;
  top: 50%;
  transform: translateY(-50%);
  width: 120px;
  height: 120px;
  border-radius: 50%;
  opacity: 0.35;
}

.tobe-card-icon {
  z-index: 1;
  position: absolute;
  left: 30px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.tobe-card--cod {
  background-color: #fff4dd;
}

.tobe-card--cod::before {
  background-color: rgba(255, 210, 140, 0.85);
}

.tobe-card--delivery {
  background-color: #e5fae9;
}

.tobe-card--delivery::before {
  background-color: rgba(134, 239, 172, 0.85);
}

.tobe-card--commission {
  background-color: #e6f3ff;
}

.tobe-card--commission::before {
  background-color: rgba(125, 180, 255, 0.85);
}

.tobe-card--invoice {
  background-color: #f5e9ff;
}

.tobe-card--invoice::before {
  background-color: rgba(196, 181, 253, 0.85);
}

.tobe-card-icon svg {
  width: 28px;
  height: 28px;
  color: #5e5873;
}

.tobe-card--cod .tobe-card-icon svg {
  color: #8d6e63;
}

.tobe-card--delivery .tobe-card-icon svg {
  color: #2e7d32;
}

.tobe-card--commission .tobe-card-icon svg {
  color: #1565c0;
}

.tobe-card--invoice .tobe-card-icon svg {
  color: #7b1fa2;
}

.tobe-card-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: 1;
  flex: 1;
  min-width: 0;
  margin-left: 90px;
}

.tobe-card-value {
  font-size: 26px;
  font-weight: 700;
  color: #222b45;
  line-height: 1.2;
  margin-bottom: 4px;
  font-family: 'Roboto', sans-serif;
}

.tobe-card-label {
  font-size: 1.125rem;
  font-weight: 400;
  color: #6b7280;
  line-height: 1.2;
  font-family: 'Roboto', sans-serif;
}

@media (max-width: 768px) {
  .tobe-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .tobe-widget-body {
    padding: 1rem;
  }

  .tobe-widget-col {
    padding: 0.375rem;
  }

  .tobe-card {
    height: 120px;
  }
}

/* Custom tooltip styling */
::v-deep .tooltip {
  opacity: 1 !important;
}

::v-deep .tooltip-inner {
  background-color: #007bff;
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  font-family: 'Roboto', sans-serif;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

::v-deep .tooltip.bs-tooltip-bottom .arrow::before {
  border-bottom-color: #007bff;
}

::v-deep .tooltip.bs-tooltip-top .arrow::before {
  border-top-color: #007bff;
}

::v-deep .tooltip.bs-tooltip-left .arrow::before {
  border-left-color: #007bff;
}

::v-deep .tooltip.bs-tooltip-right .arrow::before {
  border-right-color: #007bff;
}
</style>
